import { Button, NumberInput, Select, TextInput } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { useTranslation } from 'react-i18next';

import { FormAttachmentViewer } from 'src/Components/Forms/FormAttachmentViewer';
import LabelPicker from 'src/Components/Inputs/LabelPicker';
import { UploadFileButton } from 'src/Components/Inputs/UploadFileButton';
import { MemberSelector } from 'src/Components/Shared/MemberSelector';
import { RichTextEditor } from 'src/Components/Shared/RichTextEditor';
import { SlateNode } from 'src/Components/Shared/SlateRenderer/SlateRenderer.types';
import { useFetchStorageItemInformation } from 'src/Hooks/Mutations/useFetchStorageItemInformation';
import { DEFAULT_UPLOAD_FOLDER, useFileUploadManager } from 'src/Hooks/Shared/useFileUploadManager';
import {
  CreateProjectDtoEstimatedDurationUnitEnum,
  CreateProjectDtoPriorityEnum,
  CreateProjectDtoStatusEnum,
} from 'src/Services/API/generated/api';

import styles from './ProjectForm.module.scss';
import { Props } from './ProjectForm.types';

const ProjectForm = ({
  form,
  onSubmit,
  onCancel,
  onDelete,
  isLoading,
  isDeleting,
  isEditing,
}: Props) => {
  const { t } = useTranslation();

  const {
    uploads,
    startUpload,
    removeUpload,
    isLoading: isUploadLoading,
  } = useFileUploadManager({
    onUploadReady: readyFile => {
      if (!readyFile.fileData) {
        console.error('readyFile.fileData should not be undefined.');
        return;
      }

      const attachment = {
        video: readyFile.videoData,
        fileId: readyFile.fileData.id,
        type: readyFile.fileData.type,
        name: readyFile.fileData.name,
        previewUrl: readyFile.fileData.urls?.previewThumbnailUrl,
      };

      form.setFieldValue('attachments', current => [...current, attachment]);
    },
  });
  const { mutateAsync: fetchFileInfo } = useFetchStorageItemInformation();

  const handleExistingFilesSelect = async (fileIds: string[]) => {
    const newAttachments = await Promise.all(
      fileIds.map(async fileId => {
        const file = await fetchFileInfo(fileId);
        return {
          fileId: file.id,
          name: file.name,
          previewUrl: file.urls?.previewThumbnailUrl,
          type: file.type,
          video: file.video,
        };
      })
    );
    form.setFieldValue('attachments', current => [...current, ...newAttachments]);
  };

  return (
    <form onSubmit={onSubmit}>
      <div className={styles.formStack}>
        <Select
          label={t('common.status', 'Statut')}
          data={[
            {
              value: CreateProjectDtoStatusEnum.IdeaBox,
              label: t('projects.status.ideaBox', 'Boîte à idées'),
            },
            {
              value: CreateProjectDtoStatusEnum.IdeaToVote,
              label: t('projects.status.ideaToVote', 'À voter'),
            },
            {
              value: CreateProjectDtoStatusEnum.ProjectValidated,
              label: t('projects.status.projectValidated', 'Validé'),
            },
            {
              value: CreateProjectDtoStatusEnum.ProjectInProgress,
              label: t('projects.status.projectInProgress', 'En cours'),
            },
            {
              value: CreateProjectDtoStatusEnum.ProjectCompleted,
              label: t('projects.status.projectCompleted', 'Terminé'),
            },
          ]}
          {...form.getInputProps('status')}
        />

        <TextInput
          label={t('common.title', 'Titre')}
          placeholder={t('projects.create.titlePlaceholder', 'Ex: Rénovation cuisine')}
          required
          {...form.getInputProps('title')}
        />

        <div>
          <div className={styles.label}>{t('common.description', 'Description')}</div>
          <RichTextEditor
            value={form.values.description}
            onChange={value => form.setFieldValue('description', value as SlateNode[])}
            placeholder={t('projects.create.descriptionPlaceholder', 'Détails du projet...')}
          />
        </div>

        <div className={styles.row}>
          <NumberInput
            label={t('projects.create.estimatedCost', 'Coût estimé')}
            min={0}
            leftSection="€"
            {...form.getInputProps('estimatedCost')}
          />
          <div className={styles.row} style={{ flex: 1 }}>
            <NumberInput
              label={t('projects.create.estimatedDuration', 'Durée estimée')}
              min={0}
              style={{ flex: 1 }}
              {...form.getInputProps('estimatedDuration')}
            />
            <Select
              label="&nbsp;"
              data={[
                {
                  value: CreateProjectDtoEstimatedDurationUnitEnum.Days,
                  label: t('common.days', 'Jours'),
                },
                {
                  value: CreateProjectDtoEstimatedDurationUnitEnum.Weeks,
                  label: t('common.weeks', 'Semaines'),
                },
                {
                  value: CreateProjectDtoEstimatedDurationUnitEnum.Months,
                  label: t('common.months', 'Mois'),
                },
                {
                  value: CreateProjectDtoEstimatedDurationUnitEnum.Years,
                  label: t('common.years', 'Années'),
                },
              ]}
              style={{ width: 110 }}
              {...form.getInputProps('estimatedDurationUnit')}
            />
          </div>
        </div>

        <DatePickerInput
          label={t('projects.create.deadline', 'Date limite')}
          placeholder={t('projects.form.deadlinePlaceholder', 'Sélectionner une date')}
          clearable
          {...form.getInputProps('deadline')}
        />

        <DatePickerInput
          label={t('projects.form.completionDate', 'Date de réalisation')}
          placeholder={t('projects.form.completionDatePlaceholder', 'Sélectionner une date')}
          clearable
          {...form.getInputProps('completionDate')}
        />

        <LabelPicker
          label={t('projects.form.label', 'Étiquette')}
          value={form.values.label}
          onChange={value => form.setFieldValue('label', value)}
        />

        <Select
          label={t('projects.form.priority', 'Priorité')}
          placeholder={t('projects.form.priorityPlaceholder', 'Sélectionner une priorité')}
          clearable
          data={[
            {
              value: CreateProjectDtoPriorityEnum.High,
              label: t('projects.priority.high', 'Haute'),
            },
            {
              value: CreateProjectDtoPriorityEnum.Medium,
              label: t('projects.priority.medium', 'Moyenne'),
            },
            {
              value: CreateProjectDtoPriorityEnum.Low,
              label: t('projects.priority.low', 'Basse'),
            },
          ]}
          {...form.getInputProps('priority')}
        />

        <MemberSelector
          label={t('projects.form.responsibles', 'Responsable(s) du projet')}
          placeholder={t('projects.form.responsiblesPlaceholder', 'Sélectionner des responsables')}
          {...form.getInputProps('memberIds')}
        />

        <MemberSelector
          label={t('projects.form.participants', 'Participant(s) au projet')}
          placeholder={t('projects.form.participantsPlaceholder', 'Sélectionner des participants')}
          {...form.getInputProps('participantIds')}
        />

        <div className={styles.attachmentColumn}>
          <UploadFileButton
            onFilesSelected={files =>
              startUpload(files, DEFAULT_UPLOAD_FOLDER, 'PROJECT_ATTACHMENT')
            }
            onExistingFilesSelect={handleExistingFilesSelect}
            loading={isUploadLoading}
            modalTitle={t('project.form.attachments.modalTitle', 'Ajouter des pièces jointes')}
          />

          <FormAttachmentViewer
            attachments={form.values.attachments}
            uploadingAttachments={uploads}
            onDeleteAttachment={attachment => {
              form.setFieldValue(
                'attachments',
                form.values.attachments.filter(a => a.fileId !== attachment.fileId)
              );
            }}
            onDeleteUploadingAttachment={localId => removeUpload(localId)}
          />
        </div>

        <div className={styles.buttonGroup}>
          {isEditing && onDelete && (
            <Button variant="danger" onClick={onDelete} loading={isDeleting}>
              {t('common.delete', 'Supprimer')}
            </Button>
          )}
          <div className={styles.actions}>
            <Button variant="light" onClick={onCancel}>
              {t('common.cancel', 'Annuler')}
            </Button>
            <Button type="submit" loading={isLoading}>
              {isEditing ? t('common.save', 'Enregistrer') : t('common.create', 'Créer')}
            </Button>
          </div>
        </div>
      </div>
    </form>
  );
};

export default ProjectForm;
